import { apiDate, number, optionalString, string, uuid, zodLess, ZodLessInfer } from '@kaiizen/zod-less';
import { optionalMetadataSchema } from '../schemaLib';

export const ApiActivityLogEventTypeSchema = zodLess(
  'projectCreated'
    + '|projectCompleted'
    + '|projectReactivated'
    + '|projectArchived'
    + '|projectUnarchived'
    + '|assignmentChanged'
    + '|walkCreated'
    + '|walkUpdated'
    + '|walkCompleted'
    + '|punchlistCreated'
    + '|scopeSubmitted'
    + '|scopeApproved'
    + '|scopeRejected'
    + '|changeOrderCreated'
    + '|changeOrderUpdated'
    + '|changeOrderApproved'
    + '|changeOrderRejected'
    + '|changeOrderCanceled'
    + '|projectHandedOff'
    + '|projectWorkTaken'
    + '|managerAdded'
    + '|managerRemoved'
    + '|userCreated'
    + '|userUpdated'
    + '|userDeleted',
);
export type ApiActivityLogEventType = ZodLessInfer<typeof ApiActivityLogEventTypeSchema>;
export const activityLogEventTypes: readonly ApiActivityLogEventType[] = ApiActivityLogEventTypeSchema.options;
export const ApiActivityLogEventTypesSchema = zodLess([ApiActivityLogEventTypeSchema]);

export const ApiActivityLogSortOrderSchema = zodLess('asc|desc');
export type ApiActivityLogSortOrder = ZodLessInfer<typeof ApiActivityLogSortOrderSchema>;
export const activityLogSortOrders: readonly ApiActivityLogSortOrder[] = ApiActivityLogSortOrderSchema.options;

export const ApiScopeChangeTaskChangeActionSchema = zodLess('added|edited|removed');
export type ApiScopeChangeTaskChangeAction = ZodLessInfer<typeof ApiScopeChangeTaskChangeActionSchema>;

export const ApiScopeChangeTaskChangeSchema = zodLess({
  id: string,
  name: string,
  action: ApiScopeChangeTaskChangeActionSchema,
  totalAmount: number.optional(),
});
export type ApiScopeChangeTaskChange = ZodLessInfer<typeof ApiScopeChangeTaskChangeSchema>;

export const ApiScopeChangeGroupChangeSchema = zodLess({
  id: string,
  name: string,
  tasks: [ApiScopeChangeTaskChangeSchema],
});
export type ApiScopeChangeGroupChange = ZodLessInfer<typeof ApiScopeChangeGroupChangeSchema>;

/** The immutable task-level summary captured when a scope change is saved. */
export const ApiScopeChangeSummarySchema = zodLess({
  groups: [ApiScopeChangeGroupChangeSchema],
});
export type ApiScopeChangeSummary = ZodLessInfer<typeof ApiScopeChangeSummarySchema>;

// Retained for reading the handoff summaries written by the first iteration of
// the turn-based overview. New summaries are written as scopeChangeSummary.
export const ApiHandoffSummarySchema = ApiScopeChangeSummarySchema;
export type ApiHandoffSummary = ApiScopeChangeSummary;

export const ApiActivityLogSchema = zodLess({
  id: uuid,
  createdAt: apiDate,
  projectId: optionalString,
  orgId: uuid,
  orgMarketId: optionalString,
  orgContractorId: optionalString,
  orgProductId: optionalString,
  orgPropertyId: optionalString,
  orgSettingsId: optionalString,
  scanId: optionalString,
  scanTemplateId: optionalString,
  actorUserId: optionalString,
  actorName: optionalString,
  aboutUserId: optionalString,
  aboutUserName: optionalString,
  eventType: ApiActivityLogEventTypeSchema.optional(),
  noun: string,
  verb: string,
  details: optionalString,
  metadata: optionalMetadataSchema,
  byUserComment: optionalString,
  scopeChangeSummary: ApiScopeChangeSummarySchema.optional(),
  handoffSummary: ApiHandoffSummarySchema.optional(),

  totalAmount: number.optional(),
  previousTotalAmount: number.optional(),

  scanLifecycleStatus: optionalString,
});
export type ApiActivityLog = ZodLessInfer<typeof ApiActivityLogSchema>;
/** A project's life as a whole, as the property's activity shows it: created, completed or reopened, archived or brought back. */
export const projectLifecycleActivityEventTypes = [
  'projectCreated',
  'projectCompleted',
  'projectReactivated',
  'projectArchived',
  'projectUnarchived',
] as const satisfies readonly ApiActivityLogEventType[];

export const getPropertyActivityLogQueryKeyPrefix = (orgPropertyId?: string | null) => `activityLog/property/${orgPropertyId ?? ''}/`;
export const getPropertyActivityLogQueryKey = (orgPropertyId?: string | null, limit?: number, eventTypes?: readonly ApiActivityLogEventType[]) =>
  `${getPropertyActivityLogQueryKeyPrefix(orgPropertyId)}${limit ?? 20}${eventTypes?.length ? `?eventTypes=${eventTypes.join(',')}` : ''}`;

export const getProjectActivityLogQueryKeyPrefix = (projectId?: string | null) => `activityLog/project/${projectId ?? ''}/`;
export const getProjectActivityLogQueryKey = (projectId?: string | null, limit?: number, eventTypes?: readonly ApiActivityLogEventType[]) =>
  `${getProjectActivityLogQueryKeyPrefix(projectId)}${limit ?? 20}${eventTypes?.length ? `?eventTypes=${eventTypes.join(',')}` : ''}`;

export const ApiActivityLogListResponseSchema = zodLess({
  entries: [ApiActivityLogSchema],
  nextCursor: optionalString,
  totalCount: number.optional(),
});
export type ApiActivityLogListResponse = ZodLessInfer<typeof ApiActivityLogListResponseSchema>;
